import type { Dispatch, SetStateAction } from 'react';
import L from 'leaflet';
import type { GridParams } from 'contexts/GridContext';
import { MAX_ANALYSIS_AREAS } from 'constants/grid';
import { cancelActiveDrawing } from 'lib/leafletHelper.js';
import type { BorderType, Lv95Coordinate, ShapeType } from './cadastralShared';

interface VolumeHandlerOptions {
  analysisAreas: Lv95Coordinate[][];
  legalBorders: Lv95Coordinate[][];
  buildableBorders: Lv95Coordinate[][];
  activeAnalysisAreaIndex: number;
  gridParams: GridParams;
  activeEditBorderRef: { current: BorderType | null };
  editOriginalCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
  editDraftCoordinatesRef: { current: Partial<Record<BorderType, Lv95Coordinate[] | null>> };
  analysisAreaRef: { current: L.Polygon | null };
  centerOfAnalysisAreaRef: { current: L.Marker | null };
  isDrawingRef: { current: boolean };
  pendingDrawRef: { current: { shapeType: ShapeType | null; borderType: BorderType | null } | null };
  currentTypeOfPolygonRef: { current: BorderType | null };
  currentShapeTypeRef: { current: ShapeType | null };
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  setEditingPolygonType: Dispatch<SetStateAction<BorderType | null>>;
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  syncAnalysisAreas: (
    areas: Lv95Coordinate[][],
    options: {
      activeIndex?: number;
      analysisAreaType?: string;
      legalBorders?: Lv95Coordinate[][];
      buildableBorders?: Lv95Coordinate[][];
      recenter?: boolean;
    }
  ) => void;
  replaceActiveAnalysisArea: (coordinates: Lv95Coordinate[]) => Lv95Coordinate[][];
  replaceActiveLegalBorder: (coordinates: Lv95Coordinate[]) => Lv95Coordinate[][];
  replaceActiveBuildableBorder: (coordinates: Lv95Coordinate[]) => Lv95Coordinate[][];
  interruptBuildableOffsetMode: () => void;
  interruptLegalBorderParcelMode: () => void;
  cancelEditForBorder: (borderType: BorderType) => void;
  disableEditMode: (borderType: BorderType) => void;
}

const createCadastralVolumeHandlers = ({
  analysisAreas,
  legalBorders,
  buildableBorders,
  activeAnalysisAreaIndex,
  gridParams,
  activeEditBorderRef,
  editOriginalCoordinatesRef,
  editDraftCoordinatesRef,
  analysisAreaRef,
  centerOfAnalysisAreaRef,
  isDrawingRef,
  pendingDrawRef,
  currentTypeOfPolygonRef,
  currentShapeTypeRef,
  setShowEditableCoordinatesArea,
  setEditingPolygonType,
  setCoordinates,
  syncAnalysisAreas,
  replaceActiveAnalysisArea,
  replaceActiveLegalBorder,
  replaceActiveBuildableBorder,
  interruptBuildableOffsetMode,
  interruptLegalBorderParcelMode,
  cancelEditForBorder,
  disableEditMode,
}: VolumeHandlerOptions) => {
  const clearEditor = () => {
    setShowEditableCoordinatesArea(false);
    setEditingPolygonType(null);
    setCoordinates([]);
  };

  const clearAnalysisLayerAndMarker = () => {
    analysisAreaRef.current?.remove();
    analysisAreaRef.current = null;
    centerOfAnalysisAreaRef.current?.remove();
    centerOfAnalysisAreaRef.current = null;
  };

  const handleLinkedAnalysisAreaDelete = (deletedBorderType: BorderType) => {
    const analysisType = gridParams.analysisAreaType;
    const isLinked =
      (analysisType === 'asTheLegalBorder' && deletedBorderType === 'legalBorder') ||
      (analysisType === 'asTheBuildableBorder' && deletedBorderType === 'buildableBorder');
    if (!isLinked) return;
    syncAnalysisAreas(replaceActiveAnalysisArea([]), {
      activeIndex: activeAnalysisAreaIndex,
      analysisAreaType: '',
    });
    clearAnalysisLayerAndMarker();
  };

  const handleBorderDelete = (borderType: BorderType) => {
    if (activeEditBorderRef.current === borderType) {
      disableEditMode(borderType);
      editOriginalCoordinatesRef.current[borderType] = null;
      editDraftCoordinatesRef.current[borderType] = null;
    }
    handleLinkedAnalysisAreaDelete(borderType);
  };

  const handleDeleteActiveVolumeBorder = (borderType: Extract<BorderType, 'legalBorder' | 'buildableBorder'>) => {
    syncAnalysisAreas(analysisAreas, {
      activeIndex: activeAnalysisAreaIndex,
      ...(borderType === 'legalBorder'
        ? { legalBorders: replaceActiveLegalBorder([]) }
        : { buildableBorders: replaceActiveBuildableBorder([]) }),
    });
    handleBorderDelete(borderType);
    clearEditor();
  };

  const interruptMapActions = () => {
    interruptBuildableOffsetMode();
    if (activeEditBorderRef.current) cancelEditForBorder(activeEditBorderRef.current);
    cancelActiveDrawing({ isDrawingRef, pendingDrawRef, currentTypeOfPolygonRef, currentShapeTypeRef });
    interruptLegalBorderParcelMode();
  };

  const handleSelectAnalysisArea = (nextIndex: number) => {
    if (!analysisAreas.length) return;
    interruptMapActions();
    const boundedIndex = (nextIndex + analysisAreas.length) % analysisAreas.length;
    setShowEditableCoordinatesArea(false);
    setEditingPolygonType(null);
    syncAnalysisAreas(analysisAreas, {
      activeIndex: boundedIndex,
      analysisAreaType: analysisAreas.length > 1 ? 'newAnalysisArea' : gridParams.analysisAreaType,
    });
  };

  const handleAddAnalysisArea = () => {
    if (analysisAreas.length === 0 || analysisAreas.length >= MAX_ANALYSIS_AREAS) return;
    if (activeEditBorderRef.current) cancelEditForBorder(activeEditBorderRef.current);
    setShowEditableCoordinatesArea(false);
    setEditingPolygonType(null);
    syncAnalysisAreas([...analysisAreas, []], {
      activeIndex: analysisAreas.length,
      analysisAreaType: 'newAnalysisArea',
      legalBorders: [...legalBorders, []],
      buildableBorders: [...buildableBorders, []],
    });
  };

  const handleDeleteActiveAnalysisArea = () => {
    if (!analysisAreas.length) return;
    if (activeEditBorderRef.current) {
      disableEditMode(activeEditBorderRef.current);
      editOriginalCoordinatesRef.current[activeEditBorderRef.current] = null;
      editDraftCoordinatesRef.current[activeEditBorderRef.current] = null;
    }
    clearAnalysisLayerAndMarker();
    clearEditor();
    syncAnalysisAreas(replaceActiveAnalysisArea([]), {
      activeIndex: activeAnalysisAreaIndex,
      analysisAreaType: '',
      recenter: true,
    });
  };

  const handleDeleteVolume = (indexToDelete: number) => {
    if (analysisAreas.length <= 1 || indexToDelete < 0 || indexToDelete >= analysisAreas.length) return;
    interruptMapActions();
    if (indexToDelete === activeAnalysisAreaIndex) clearAnalysisLayerAndMarker();

    const nextAreas = analysisAreas.filter((_, index) => index !== indexToDelete);
    const nextLegalBorders = legalBorders.filter((_, index) => index !== indexToDelete);
    const nextBuildableBorders = buildableBorders.filter((_, index) => index !== indexToDelete);
    const nextActiveIndex = Math.max(0, Math.min(indexToDelete, nextAreas.length - 1));
    clearEditor();
    syncAnalysisAreas(nextAreas, {
      activeIndex: nextActiveIndex,
      legalBorders: nextLegalBorders,
      buildableBorders: nextBuildableBorders,
      analysisAreaType: nextAreas.length > 1 ? 'newAnalysisArea' : gridParams.analysisAreaType,
      recenter: true,
    });
  };

  return {
    handleBorderDelete,
    handleDeleteActiveVolumeBorder,
    handleSelectAnalysisArea,
    handleAddAnalysisArea,
    handleDeleteActiveAnalysisArea,
    handleDeleteVolume,
  };
};

export default createCadastralVolumeHandlers;
